"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X, CircleCheck, ArrowRight, Zap } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const traditionalFeatures = ["Assembly-line care and long wait times", "Hidden fees and generic dental materials", "Rotating doctors and impersonal service", "Limited focus on patient comfort", "Complex and confusing billing cycles"];
const modernFeatures = ["Personalized care from El Monte experts", "Affordable, transparent flat-rate pricing", "Same-day repairs and precision implants", "Specialized sedation for anxiety-free visits", "Local community focus and dedicated staff"];
export default function ModernMeetup() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: leftCardRef,
    isVisible: isLeftCardVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: rightCardRef,
    isVisible: isRightCardVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section data-section-id="193049" id="whypatientschoosesmartchoice" className="py-24 bg-background relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Badge variant="outline" className="mb-4 text-primary border-primary/20 bg-primary/5">
            The Smart Choice
          </Badge>
          <h2 className="text-3xl md:text-5xl font-default font-bold text-foreground tracking-tight mb-6">
            Why Patients Choose Smart Choice over <br className="hidden md:block" />
            <span className="font-serif italic text-primary font-normal">Large Implant Centers</span>
          </h2>
          <p className="text-muted-foreground text-lg font-default">
            Experience the difference of personalized, affordable care right here in El Monte. We prioritize your comfort and long-term oral health over corporate quotas.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-stretch max-w-5xl mx-auto">
          {}
          <div ref={leftCardRef} className={`transition-all duration-700 delay-100 ${isLeftCardVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <Card className="h-full bg-muted/30 border-border/50 shadow-none flex flex-col opacity-80 hover:opacity-100 transition-opacity">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-default text-muted-foreground flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <span className="text-muted-foreground text-sm font-bold">01</span>
                  </span>
                  Corporate Dental Centers
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-grow">
                <ul className="space-y-4">
                  {traditionalFeatures.map((feature, i) => <li key={`trad-${i}`} data-index={i} className="flex items-start gap-3 text-muted-foreground font-default">
                      <X className="w-5 h-5 text-muted-foreground/50 shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>)}
                </ul>
              </CardContent>
            </Card>
          </div>

          {}
          <div ref={rightCardRef} className={`transition-all duration-700 delay-300 ${isRightCardVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="relative h-full group">
              {}
              <div className="absolute -inset-0.5 bg-gradient-to-br from-primary/30 to-secondary/30 rounded-2xl blur-xl opacity-50 group-hover:opacity-70 transition-opacity duration-500" />
              
              <Card className="relative h-full bg-background/80 backdrop-blur-xl border-primary/20 shadow-2xl shadow-primary/5 flex flex-col overflow-hidden">
                {}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-secondary" />
                
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="default" className="bg-primary/10 text-primary hover:bg-primary/20 border-none">
                      <Zap className="w-3 h-3 mr-1" /> The Local Advantage
                    </Badge>
                  </div>
                  <CardTitle className="text-2xl font-default font-bold text-foreground flex items-center gap-2">
                    Smart Choice Experience
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow">
                  <ul className="space-y-4">
                    {modernFeatures.map((feature, i) => <li key={`mod-${i}`} data-index={i} className="flex items-start gap-3 text-foreground font-default font-medium">
                        <CircleCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>)}
                  </ul>
                </CardContent>
                <CardFooter className="pt-6 border-t border-border/50 bg-muted/10">
                  <Button asChild size="lg" className="w-full rounded-full shadow-lg shadow-primary/20 group bg-primary text-primary-foreground hover:bg-primary/90">
                    <Link to="#">
                      Free Consultation
                      <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>;
}